import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getFeatureSchema,
  getBreadcrumbSchema
} from '../../utils/seoSchemas';

const steps = [
  'Technician opens the digital health check for the vehicle',
  'Works through each inspection point with red/amber/green status',
  'Takes photos or videos of any issues found',
  'Adds notes explaining work needed',
  'Sends the report to admin for pricing',
  'Customer receives clear visual evidence of recommended work'
];

const benefits = [
  'Build customer trust with photo and video evidence',
  'Standard inspection checklist ensures nothing is missed',
  'Red/amber/green status makes priorities clear',
  'Mobile-friendly for technicians on the shop floor',
  'Digital records for every vehicle inspection',
  'Seamless handoff to admin for pricing'
];

const VehicleHealthChecks = () => {
  const featureSchema = getFeatureSchema({
    name: 'Vehicle Health Checks',
    description: 'Digital eVHC with photo and video uploads. Communicate vehicle issues clearly to customers with visual evidence.',
    url: 'https://www.go-ops.digital/features/vehicle-health-checks'
  });
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Features', url: 'https://www.go-ops.digital/features' },
    { name: 'Vehicle Health Checks', url: 'https://www.go-ops.digital/features/vehicle-health-checks' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Vehicle Health Checks | Go Ops Features</title>
        <meta
          name="description"
          content="Digital eVHC with photo and video uploads. Communicate vehicle issues clearly to customers with visual evidence and build trust."
        />
        <link rel="canonical" href="https://www.go-ops.digital/features/vehicle-health-checks" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Vehicle Health Checks | Go Ops Features" />
        <meta
          property="og:description"
          content="Digital eVHC with photo and video uploads. Communicate vehicle issues clearly to customers with visual evidence."
        />
        <meta property="og:url" content="https://www.go-ops.digital/features/vehicle-health-checks" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify(featureSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Vehicle Health Checks
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Digital eVHC with photo and video evidence
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4, lineHeight: 1.8 }}>
          Go Ops provides digital vehicle health checks (eVHC) that let technicians document issues with
          photos and videos. Customers see exactly what needs attention, building trust and increasing
          approval rates for recommended work. Every inspection is recorded digitally for future reference.
        </Typography>

        {/* How it works */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            How It Works
          </Typography>
          <List>
            {steps.map((step, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#ff6b35',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText primary={step} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Benefits */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            Benefits
          </Typography>
          <List>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#28a745' }} />
                </ListItemIcon>
                <ListItemText primary={benefit} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            See vehicle health checks in action
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/features"
              sx={{ borderColor: '#2c3e50', color: '#2c3e50' }}
            >
              View All Features
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/pricing" sx={{ color: 'white', textTransform: 'none' }}>
                Pricing
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VehicleHealthChecks;
